import React from 'react';
import {
  Panel,
  type PanelImperativeHandle,
  type PanelSize,
} from 'react-resizable-panels';

/** v4 mixed units: a number is pixels, a string is a CSS size (`"55%"`). */
type PanelSizeProp = number | string;

export type GroupResizeBehavior =
  | 'preserve-relative-size'
  | 'preserve-pixel-size';

export type ResizablePanelProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
  collapsible?: boolean;
  collapsedSize?: PanelSizeProp;
  minSize?: PanelSizeProp;
  maxSize?: PanelSizeProp;
  defaultSize?: PanelSizeProp;
  /**
   * How this panel reacts when the group is resized (window chrome, parent
   * layout). Default is relative. Pixel-sized chrome (sidebar, context bar)
   * should preserve pixels so a user width does not scale with the viewport.
   * A group still needs at least one relative panel to absorb leftover space.
   */
  groupResizeBehavior?: GroupResizeBehavior;
  onResize?: (sizeInPixels: number, prevSizeInPixels?: number) => void;
  onCollapse?: () => void;
  onExpand?: () => void;
};

/** v4 reads bare numbers as pixels; this is the only percent conversion. */
export const toPercent = (value: number): `${number}%` => `${value}%`;

export const ResizablePanel = React.forwardRef<
  PanelImperativeHandle,
  ResizablePanelProps
>(
  (
    {
      children,
      className,
      id,
      collapsible,
      collapsedSize,
      minSize,
      maxSize,
      defaultSize,
      groupResizeBehavior,
      onResize,
      onCollapse,
      onExpand,
    },
    ref,
  ) => {
    const collapsedSizePx =
      typeof collapsedSize === 'number' ? collapsedSize : 0;
    const handleResize = React.useCallback(
      (
        size: PanelSize,
        _id: string | number | undefined,
        prevSize: PanelSize | undefined,
      ) => {
        onResize?.(size.inPixels, prevSize?.inPixels);
        if (!collapsible) return;
        // v4 dropped onCollapse/onExpand; recreate the v2 semantics, including
        // firing onCollapse when a persisted layout restores collapsed on mount.
        const isNowCollapsed = size.inPixels === collapsedSizePx;
        const wasCollapsed =
          prevSize === undefined
            ? undefined
            : prevSize.inPixels === collapsedSizePx;
        if (isNowCollapsed && wasCollapsed !== true) {
          onCollapse?.();
        } else if (!isNowCollapsed && wasCollapsed === true) {
          onExpand?.();
        }
      },
      [onResize, onCollapse, onExpand, collapsible, collapsedSizePx],
    );
    return (
      <Panel
        panelRef={ref}
        id={id}
        collapsible={collapsible}
        collapsedSize={collapsedSize}
        minSize={minSize}
        maxSize={maxSize}
        defaultSize={defaultSize}
        groupResizeBehavior={groupResizeBehavior}
        onResize={handleResize}
        className={className}
        // v2 panels clipped their content; v4's inner div defaults to overflow
        // auto, so restore hidden here (inline style wins over the default).
        style={{ overflow: 'hidden' }}
      >
        {children}
      </Panel>
    );
  },
);
ResizablePanel.displayName = 'ResizablePanel';
